// initialize TrackJS as soon as Apollo is set up
import "@digits-shared/initializers/errorReporting"
import { ApolloClient, ApolloLink, type FetchPolicy, HttpLink, split } from "@apollo/client"
import { BatchHttpLink } from "@apollo/client/link/batch-http"
import { type HttpOptions } from "@apollo/client/link/http/selectHttpOptionsAndBody"
import { getMainDefinition } from "@apollo/client/utilities"
import { type GraphQLTrace } from "@digits-shared/components/Contexts/GraphQLTracerContext"
import type Session from "@digits-shared/session/Session"
import { Kind, OperationTypeNode } from "graphql"
import httpBatch from "@digits-shared/initializers/apollo/middleware/http/batch"
import httpBreakVersion from "@digits-shared/initializers/apollo/middleware/http/breakingVersion"
import dgMutationBlocker from "@digits-shared/initializers/apollo/middleware/http/dgMutationBlocker"
import httpError from "@digits-shared/initializers/apollo/middleware/http/error"
import httpGraphQLTrace from "@digits-shared/initializers/apollo/middleware/http/graphQLTrace"
import httpRetry from "@digits-shared/initializers/apollo/middleware/http/retry"
import httpTokenUpdate from "@digits-shared/initializers/apollo/middleware/http/tokenUpdate"
import wsAuth from "@digits-shared/initializers/apollo/middleware/ws/auth"
import wsRetry from "@digits-shared/initializers/apollo/middleware/ws/retry"
import WSLink from "@digits-shared/initializers/apollo/middleware/ws/wsLink"
import { cache } from "src/shared/initializers/apollo/apolloCache"
import frontendAuth from "src/shared/initializers/apollo/frontendAuth"
import { isDeveloperToolsEnabled } from "src/shared/components/DeveloperTools/useDeveloperTools"
import { employeeFeatureKeys } from "src/shared/config/employeeFeatures"

export interface ApolloClientOptions {
  defaultFetchPolicy?: FetchPolicy
  neverBatch?: boolean
  withWS?: boolean
  onTrace?: (trace: GraphQLTrace) => void
}

export function setupApolloClient<S extends Session>(session: S, options?: ApolloClientOptions) {
  const {
    defaultFetchPolicy = "cache-first",
    neverBatch = (session.isDigitsEmployee &&
      session.getBooleanUserPreference(employeeFeatureKeys.graphqlNoBatching)) ||
      false,
    withWS = false,
  } = options || {}

  const httpOptions: HttpOptions = {
    uri: process.env.GRAPHQL_ENDPOINT,

    // Enable cross-origin referrers for apollo originated requests,
    // which will only occur to the above-configured uri. This enables
    // greatly improved debug visibility into the specific page the user
    // was on when an error was generated.
    fetchOptions: { referrerPolicy: "no-referrer-when-downgrade" },
  }

  const singleHttpRequestLink = new HttpLink(httpOptions)
  const batchHttpRequestLink = new BatchHttpLink(httpOptions)
  const avsBatchHttpRequestLink = new BatchHttpLink(httpOptions)

  // Dedicate link chain for HTTP requests
  const httpLinkChain = ApolloLink.from([
    // Block mutations if user is a DG
    dgMutationBlocker(session, options?.onTrace),
    // Error first, so any downstream issues are handled
    httpError(session),
    // Then retry, so any failures downstream are retried
    httpRetry(),
    frontendAuth(session, {
      bearerGraphqlEndpoint: process.env.GRAPHQL_ENDPOINT,
      payGraphqlEndpoint: process.env.GRAPHQL_PAY_ENDPOINT,
      publicGraphqlEndpoint: process.env.GRAPHQL_PUBLIC_ENDPOINT,
    }),
    httpBreakVersion(),
    httpBatch(neverBatch),
    httpGraphQLTrace(options?.onTrace, () => isDeveloperToolsEnabled(session)),
    httpTokenUpdate(session),
    split(
      (operation) => operation.getContext().noBatch === true || neverBatch,
      singleHttpRequestLink,
      split(
        (operation) => operation.getContext().avsBatch === true,
        avsBatchHttpRequestLink,
        batchHttpRequestLink
      )
    ),
  ])

  // Dedicate link chain for WS requests.
  const wsLinkChain = ApolloLink.from([
    httpError(session),
    wsRetry(session),
    // Auth after retry so that retries always have a valid token.
    wsAuth(session),
    new WSLink(session, withWS, process.env.GRAPHQL_WEBSOCKET_ENDPOINT || ""),
  ])

  const defaultOptions = {
    watchQuery: { fetchPolicy: defaultFetchPolicy },
  }

  return new ApolloClient({
    assumeImmutableResults: true,

    // If the connection request is a subscription, use wsLink. Otherwise, use http link chain.
    link: split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === Kind.OPERATION_DEFINITION &&
          definition.operation === OperationTypeNode.SUBSCRIPTION
        )
      },
      // Temporary workaround to suppress errors caused when a Digits Employee logs in.
      // This causes them to send a subscription operation even though the network client was
      // initialized without WS support.
      withWS ? wsLinkChain : ApolloLink.empty(),
      httpLinkChain
    ),
    cache,
    defaultOptions,
  })
}
